import { Injectable } from "@angular/core";
import { AngularFireAnalytics } from "@angular/fire/compat/analytics";
import {
  AnalyticsCallOptions,
  getGoogleAnalyticsClientId,
} from "firebase/analytics";
import { console } from "#/log";
import { ActivatedRoute } from "@angular/router";
import { UnrecoverableStateEvent, VersionEvent } from "@angular/service-worker";
import { fetchBuildVersion } from "#/fetchBuildVersion";
import { AppService } from "#/app/app.service";
import { Platform } from "@ionic/angular";
import { Chem } from "#/declarations";

type CustomEventName = (typeof AnalyticsService)["CUSTOM_EVENT_NAMES"][number];
/**
 * Injectable service for pushing analytics data to google.
 *
 * Uses Firebase integration for Google Analytics.
 *
 * @see {@link https://github.com/angular/angularfire/blob/v6/docs/analytics/getting-started.md}
 *
 * @see {@link https://support.google.com/analytics/answer/9234069|Firebase integration - Automatically collected events}
 * @see {@link https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtag|gtag standard/recommended events}
 * @see {@link https://firebase.google.com/docs/reference/js/analytics.eventparams|EventParams interface documentation}
 */
@Injectable()
export class AnalyticsService {
  /**
   * Custom event names defined by this app.
   *
   * Per {@link https://support.google.com/analytics/answer/13316687|GA4 Event Naming Rules}:
   * - Event names are case sensitive. For example, my_event and My_Event are distinct events.
   * - Event names can include English and non-English words and letters.
   * - Do not use reserved prefixes and event names.
   *
   * - Use only LETTERS, NUMBERS, and UNDERSCORES. Don't use spaces.
   * - Event names must start with a letter.
   *
   * When adding, check against the
   * {@link https://support.google.com/analytics/answer/13316687#zippy=%2Cweb|list of reserved names}
   */
  public static readonly CUSTOM_EVENT_NAMES = [
    "chem_peak",
    "chem_build",
    "chem_notes",
    "sw_version",
    "user_reset_progress",
    "user_signout",
    "show_pwa_install",
    "show_certificate",
    "data_sync",
  ] as const;

  public static isCustomEventName(id: string): id is CustomEventName {
    return (this.CUSTOM_EVENT_NAMES as readonly string[]).includes(id);
  }

  // private user: any = null;

  constructor(
    protected activatedRoute: ActivatedRoute,
    protected analytics: AngularFireAnalytics,
    protected platform: Platform,
    protected app: AppService,
  ) {
    // this.analytics.updateConfig({APP_NAME:  }) .then((clientId) =>
    //   console.debug(`analytics.service: initialized; GA client ID:`, clientId),
    // );
    // this.authService.getAuth().onAuthStateChanged((user: any) => {
    //   this.user = user;
    // });
    this.analytics.app
      .then((app) => {
        const a = app.analytics();
        console.debug("analytics instance:", a);
        return getGoogleAnalyticsClientId(a);
      })
      .then((clientId) => console.debug("analytics client ID:", clientId));

    this.analytics.updateConfig({ is_pwa: this.platform.is("pwa") });
  }

  /**
   * The service worker has reported a version update event
   */
  public trackSwVersionEvent(event: VersionEvent) {
    if (event.type === "VERSION_INSTALLATION_FAILED") {
      return this.analytics.logEvent("exception", {
        description: `${event.type} [${event.version}]: ${event.error}`,
        fatal: false,
      });
    } else {
      const eventId: CustomEventName = "sw_version";
      switch (event.type) {
        case "VERSION_DETECTED":
          return this.logCustomEvent(
            eventId,
            {
              type: event.type,
              versionHash: event.version.hash,
              versionAppData: JSON.stringify(event.version.appData),
            },
            { global: true },
          );
        case "VERSION_READY":
          return this.logCustomEvent(
            eventId,
            {
              type: event.type,
              latestVersionHash: event.latestVersion.hash,
              latestVersionAppData: JSON.stringify(event.latestVersion.appData),
            },
            { global: true },
          );
      }
    }
    return Promise.resolve();
  }

  /** An unrecoverable condition has been reported by the service worker */
  public trackSwUnrecoverable(event: UnrecoverableStateEvent) {
    return this.analytics.logEvent("exception", {
      description: `${event.type}: ${event.reason}`,
      fatal: true,
    });
  }

  /**
   * An achievement has been unlocked (by ID)
   *
   * @see {@link https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtag#unlock_achievement}
   */
  public trackAchievement(achievement_id: string) {
    return this.analytics.logEvent("unlock_achievement", { achievement_id });
  }

  /**
   * User has reset their progress data
   */
  public trackResetProgress() {
    return this.logCustomEvent("user_reset_progress");
  }

  /**
   * User is explicitly signing out
   */
  public trackSignOut() {
    return this.logCustomEvent("user_signout");
  }

  /**
   * User is getting info on installing to home screen
   */
  public trackShowPwaInstall() {
    return this.logCustomEvent("show_pwa_install");
  }

  /**
   * User starts working on a sample
   *
   * @param sampleId sample ID
   *
   * @see {@link https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtag#level_start}
   */
  public trackSampleStart(sampleId: string) {
    return this.analytics.logEvent("level_start", { level_name: sampleId });
  }

  /**
   * User completes a sample
   *
   * @param sampleId sample ID
   * @param [success] default true (exposed because it's a param of the `level_end` event)
   *
   * @see {@link https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtag#level_end}
   */
  public trackSampleComplete(sampleId: string, success: boolean = true) {
    return this.analytics.logEvent("level_end", {
      level_name: sampleId,
      success,
    });
  }

  /** Wrapper for logging custom events */
  protected logCustomEvent(
    eventName: CustomEventName,
    eventParams?: Record<string, any>,
    options?: AnalyticsCallOptions,
  ) {
    if (!eventName.match(/^[A-Za-z]\w*$/)) {
      console.warn(
        `Analytics custom event name '${eventName}' is invalid and may be unusable. See https://support.google.com/analytics/answer/13316687.`,
      );
    }
    return this.analytics.logEvent(eventName, eventParams, options);
  }

  /**
   * User requested a certificate
   * @param completed
   */
  public trackCertificate(completed: string[]) {
    return this.logCustomEvent("show_certificate", {
      completed: completed.join(","),
    });
  }

  /** Track exceptions and error conditions */
  public trackError(
    description: string,
    detail?: string | Error | unknown,
    fatal?: boolean,
  ) {
    let desc = description;
    if (detail instanceof Error) {
      desc += "\n" + detail.name + "\n" + detail.message;
    } else if (typeof detail === "string") {
      desc += "\n" + detail;
    } else if (detail) {
      desc += "\n" + String(detail);
    }
    return this.analytics.logEvent("exception", {
      description: desc,
      fatal,
    });
  }

  /** User data was out of sync between local and remote.  */
  public trackDataSync(
    outOfSync: "remote" | "local",
    sync?:
      | "interactive-confirmed" /** User prompted to sync (remote -> local) and confirmed */
      | "interactive-rejected" /** User prompted to sync (remote -> local) and rejected */
      | "automatic" /** Synced automatically (local->remote or remote->local when local has no data) */,
  ) {
    return this.logCustomEvent("data_sync", { outOfSync, sync });
  }

  /** User assigned a group to a range for a sample */
  public trackPeak(
    sampleId: string | undefined,
    rangeId: string | undefined,
    group: Chem.Group | undefined,
    correct: boolean | null,
  ) {
    return this.logCustomEvent("chem_peak", {
      sampleId: sampleId ?? "[unknown]",
      rangeId: rangeId ?? "[unknown]",
      group: group?.id ?? "[unknown]",
      correct: correct ?? "[unknown]",
    });
  }

  public trackBuild(
    sampleId: string | undefined,
    groupId: string | undefined,
    operation: "add" | "remove",
    explicit: boolean,
    empty: boolean,
  ) {
    return this.logCustomEvent("chem_build", {
      sampleId: sampleId ?? "[unknown]",
      group: groupId ?? "[unknown]",
      operation,
      explicit: explicit ?? "[unknown]",
      empty: empty ?? "[unknown]",
    });
  }
}
